import { useState, useCallback } from 'react';
import { useApiFetch } from '../api';
import { VmConsoleData } from '../types/models/VirtualMachine';
import { ApiResponseVmConsole } from '../types/api/ApiResponseVirtualMachine';

interface UseVmConsoleResult {
  consoleData: VmConsoleData | null;
  isLoading: boolean;
  error: Error | null;
  requestConsole: () => void;
  reset: () => void;
}

export const useVmConsole = (vmId: number): UseVmConsoleResult => {
  const [consoleData, setConsoleData] = useState<VmConsoleData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const apiFetch = useApiFetch();

  const requestConsole = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setConsoleData(null);

    try {
      const response = await apiFetch(`/datacenters/vms/${vmId}/console`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmConsole;
      if (data.status === 'success') {
        setConsoleData(data.data);
      } else {
        throw new Error(data.message || 'Failed to request console');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to request console'));
    } finally {
      setIsLoading(false);
    }
  }, [vmId, apiFetch]);

  const reset = useCallback(() => {
    setConsoleData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { consoleData, isLoading, error, requestConsole, reset };
};
